import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { CastState, CastAction } from '../types';

// Initial state
const initialState: CastState = {
  agents: [],
  selectedAgent: null,
  jobs: [],
  loading: {
    agents: false,
    job: false,
    creation: false,
  },
  error: null,
};

// Reducer function
const castReducer = (state: CastState, action: CastAction): CastState => {
  switch (action.type) {
    case 'LOAD_AGENTS_START':
      return {
        ...state,
        loading: { ...state.loading, agents: true },
        error: null,
      };

    case 'LOAD_AGENTS_SUCCESS':
      return {
        ...state,
        agents: action.payload,
        loading: { ...state.loading, agents: false },
        error: null,
      };

    case 'LOAD_AGENTS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, agents: false },
        error: action.payload,
      };

    case 'SELECT_AGENT':
      return {
        ...state,
        selectedAgent: action.payload,
      };

    case 'CREATE_AGENT_START':
      return {
        ...state,
        loading: { ...state.loading, creation: true },
        error: null,
      };

    case 'CREATE_AGENT_SUCCESS':
      return {
        ...state,
        jobs: [...state.jobs, action.payload],
        loading: { ...state.loading, creation: false },
        error: null,
      };

    case 'CREATE_AGENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, creation: false },
        error: action.payload,
      };

    case 'UPDATE_JOB_STATUS':
      return {
        ...state,
        jobs: state.jobs.map(job =>
          job.job_id === action.payload.job_id ? action.payload : job
        ),
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Context
const CastContext = createContext<{
  state: CastState;
  dispatch: React.Dispatch<CastAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

// Provider component
export const CastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(castReducer, initialState);

  return (
    <CastContext.Provider value={{ state, dispatch }}>
      {children}
    </CastContext.Provider>
  );
};

// Hook to use the context
export const useCastContext = () => {
  const context = useContext(CastContext);
  if (!context) {
    throw new Error('useCastContext must be used within a CastProvider');
  }
  return context;
};